import type { Graph, GraphType, Series } from '#models';
import type { GraphBlock } from '#models';

export function isComparisonHistoryChart(title: string | undefined): boolean {
  return title !== undefined && /\bpreceding\s+comparison\s+history\b/i.test(title);
}

export function normalizeGraphBlock(block: Record<string, unknown>): GraphBlock | null {
  const candidate =
    block['graph'] && typeof block['graph'] === 'object' && !Array.isArray(block['graph'])
      ? (block['graph'] as Record<string, unknown>)
      : block;

  const graphType = toGraphType(candidate['graphType']);
  if (!graphType) {
    return null;
  }

  const xAxis = normalizeAxis(candidate['xAxis']);
  const yAxis = normalizeAxis(candidate['yAxis']);
  const series = normalizeSeries(candidate['series']);

  if (!xAxis || !yAxis || series.length === 0) {
    return null;
  }

  const graph: Graph = {
    graphType,
    title: String(candidate['title'] ?? ''),
    xAxis,
    yAxis,
    series,
  };

  return { type: 'graph', graph };
}

function toGraphType(raw: unknown): GraphType | null {
  const value = String(raw ?? '').toLowerCase();
  switch (value) {
    case 'line':
    case 'bar':
    case 'area':
    case 'scatter':
      return value;
    case 'timegraph':
      return 'timeGraph';
    default:
      return null;
  }
}

function normalizeAxis(raw: unknown): Graph['xAxis'] | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const axis = raw as Record<string, unknown>;
  return {
    label: String(axis['label'] ?? ''),
    uom: String(axis['uom'] ?? ''),
  };
}

function normalizeSeries(raw: unknown): Series[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((entry): Series | null => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return null;
      }

      const series = entry as Record<string, unknown>;
      const rawData = Array.isArray(series['data']) ? (series['data'] as unknown[]) : [];
      const data = rawData
        .map((point): { x: number; y: number } | null => {
          if (!point || typeof point !== 'object' || Array.isArray(point)) {
            return null;
          }

          const value = point as Record<string, unknown>;
          const x = Number(value['x']);
          const y = Number(value['y']);
          if (!Number.isFinite(x) || !Number.isFinite(y)) {
            return null;
          }
          return { x, y };
        })
        .filter((point): point is { x: number; y: number } => point !== null);

      if (data.length === 0) {
        return null;
      }

      return {
        id: String(series['id'] ?? ''),
        name: String(series['name'] ?? ''),
        color: series['color'] != null ? String(series['color']) : undefined,
        data,
      };
    })
    .filter((series): series is Series => series !== null);
}
